import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { 
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Chip, IconButton, Tooltip, CircularProgress, Alert, 
  TextField, MenuItem, FormControl, InputLabel, Select, Grid, Divider, 
  Collapse, Pagination, InputAdornment, Card, CardContent, CardActions,
  useMediaQuery, Stack
} from '@mui/material';
import { 
  Add, Visibility, Edit, Delete, Search, FilterList, ExpandMore, 
  ExpandLess, Refresh 
} from '@mui/icons-material';
import useFetchObjects from '../../api/useFetchObjects';
import useDelete from '../../api/useDelete';
import { useAuth } from '../../context/AuthContext';
import LocalizedDate from '../../components/common/LocalizedDate';
import LocalizedDatePicker from '../../components/common/LocalizedDatePicker';
import { useTheme } from '@mui/material/styles';
import { RTLTextField, RTLFormControl, RTLButton } from '../../styles/rtlComponents';
import useRTL from '../../hooks/useRTL';

const DocumentList = () => {
  const { t, i18n } = useTranslation(['documents', 'common']);
  const { user } = useAuth();
  const { isRTL, getRTLProps } = useRTL();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State management
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [taskStatusFilter, setTaskStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sourceTypeFilter, setSourceTypeFilter] = useState('');
  const [fromDateFilter, setFromDateFilter] = useState('');
  const [toDateFilter, setToDateFilter] = useState('');
  const [isConfidentialFilter, setIsConfidentialFilter] = useState('');
  const [isArchivedFilter, setIsArchivedFilter] = useState('');

  // API data fetching - memoize the parameters to prevent unnecessary refetches
  const apiParams = useMemo(() => {
    const params = {
      page, 
      limit, 
      search, 
      type: typeFilter, 
      taskStatus: taskStatusFilter, 
      priority: priorityFilter,
      sourceType: sourceTypeFilter,
      isConfidential: isConfidentialFilter === 'true' ? true : isConfidentialFilter === 'false' ? false : undefined,
      isArchived: isArchivedFilter === 'true' ? true : isArchivedFilter === 'false' ? false : undefined 
    };
    
    // Only include date filters if they are explicitly set (not empty strings)
    if (fromDateFilter && fromDateFilter.trim() !== '') {
      params.fromDate = fromDateFilter;
    }
    if (toDateFilter && toDateFilter.trim() !== '') {
      params.toDate = toDateFilter;
    }
    
    console.log('DocumentList API Params:', params);
    return params;
  }, [page, limit, search, typeFilter, taskStatusFilter, priorityFilter, sourceTypeFilter, fromDateFilter, toDateFilter, isConfidentialFilter, isArchivedFilter]);

  const { data: documentsData, isLoading, isError, error, refetch } = useFetchObjects(
    'documents', 
    apiParams,
    user?.token
  );


  // Fetch task assignments for documents on the current page
  const documentIds = documentsData?.documents?.map(doc => doc.id).join(',') || '';
  console.log('Document IDs for task assignments:', documentIds);
  console.log('Documents data:', documentsData);
  
  const { data: taskAssignmentsData, isLoading: taskAssignmentsLoading, error: taskAssignmentsError } = useFetchObjects(
    'task-assignments/by-document-ids',
    { document_ids: documentIds },
    user?.token,
    { enabled: Boolean(documentIds) }
  );
  
  console.log('Task assignments data:', taskAssignmentsData);
  console.log('Task assignments loading:', taskAssignmentsLoading);
  console.log('Task assignments error:', taskAssignmentsError);
  console.log('User token:', user?.token ? 'Present' : 'Missing');

  // Filter options
  // Filter option values aligned with backend enums (Persian for type/priority)
  const documentTypes = ['مکتوب', 'استعلام', 'پیشنهاد', 'گزارش']
    .map(v => ({ value: v, label: v }));

  const taskStatuses = ['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'REJECTED']
    .map(s => ({ value: s, label: t(`tasks:status_${s.toLowerCase()}`, s) }));

  const documentPriorities = ['عاجل', 'اطمینانیه', 'ابلاغیه', 'محرم', 'عادي']
    .map(v => ({ value: v, label: v }));

  const sourceTypes = ['INTERNAL', 'EXTERNAL', 'PROVINCIAL']
    .map(v => ({ value: v, label: t(`documents:source_${v.toLowerCase()}`, v) }));

  // Helper functions
  const statusDefaultsPs = {
    ASSIGNED: 'استول شوی',
    IN_PROGRESS: 'په جریان کی',
    COMPLETED: 'بشپړ شوی',
    REJECTED: 'رد شوی',
    REJECTED_RECEIPT: 'رد شوی',
    FORWARDED: 'راجع شوی',
    ACCEPTED: 'استول شوی',
    RECEIVED: 'استول شوی'
  };

  const getStatusColor = (status) => ({
              // Document workflow statuses (if ever shown here)
    DRAFT: 'default', SUBMITTED: 'info', IN_REVIEW: 'warning',
    APPROVED: 'success', CANCELLED: 'secondary',
    // Task assignment statuses – aligned with TaskAssignedByme.jsx
    ASSIGNED: 'default',
    ACCEPTED: 'info',
    RECEIVED: 'info',
    IN_PROGRESS: 'primary',
    COMPLETED: 'success',
    REJECTED: 'error',
    REJECTED_RECEIPT: 'error',
    FORWARDED: 'warning',
    SKIPPED: 'secondary'
  }[status] || 'default');

  const getPriorityColor = (priority) => ({
    'عادي': 'default',
    'اطمینانیه': 'info',
    'ابلاغیه': 'warning',
    'محرم': 'secondary',
    'عاجل': 'error'
  }[priority] || 'default');

  const getStatusLabel = (status) => {
    const key = `tasks:status_${status.toLowerCase()}`;
    // Use i18n translation if available; otherwise fall back to Pashto defaults then status
    const translated = t(key, statusDefaultsPs[status] || status);
    return translated;
  };

  const { handleDelete, ConfirmDialog } = useDelete('documents', user?.token);

  // Modern Mobile Card Component
  const DocumentCard = ({ doc, index }) => (
    <Card sx={{ 
      mb: 3, 
      borderRadius: 3,
      boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
      border: '1px solid',
      borderColor: 'divider',
      overflow: 'hidden',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        transform: 'translateY(-4px)',
        borderColor: 'primary.main'
      }
    }}>
      {/* Card Header with Status Indicator */}
      <Box sx={{ 
        position: 'relative',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        color: 'white',
        p: 2,
        pb: 1
      }}>
        {/* Status Badge */}
        <Box sx={{ 
          position: 'absolute', 
          top: 12, 
          [isRTL ? 'left' : 'right']: 12,
          display: 'flex',
          gap: 0.5
        }}>
          {doc.is_confidential && (
            <Chip 
              label={t('documents:confidential')} 
              color="error" 
              size="small" 
              sx={{ 
                height: 24, 
                fontSize: '0.7rem',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: 'error.main',
                fontWeight: 600
              }} 
            />
          )}
        </Box>

        {/* Document Number */}
        <Typography 
          variant="caption" 
          sx={{ 
            opacity: 0.8,
            fontSize: '0.75rem',
            fontWeight: 500,
            textAlign: isRTL ? 'right' : 'left'
          }}
        >
          #{((page - 1) * limit + index + 1)}
        </Typography>

        {/* Document Title */}
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700,
            fontSize: '1.1rem',
            lineHeight: 1.3,
            mt: 0.5,
            textAlign: isRTL ? 'right' : 'left',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {doc.title}
        </Typography>
      </Box>

      <CardContent sx={{ p: 3, pt: 2 }}>
        {/* Quick Info Grid */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
          mb: 3
        }}>
          {/* Document Type */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 0.5,
            textAlign: isRTL ? 'right' : 'left'
          }}>
            <Typography variant="caption" sx={{ 
              fontWeight: 600, 
              color: 'text.secondary',
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {t('documents:documentType')}
            </Typography>
            <Chip 
              label={doc.type} 
              size="small" 
              sx={{ 
                alignSelf: isRTL ? 'flex-end' : 'flex-start',
                backgroundColor: `${getPriorityColor(doc.type)}.100`,
                color: `${getPriorityColor(doc.type)}.main`,
                fontWeight: 600,
                fontSize: '0.75rem'
              }} 
            />
          </Box>

          {/* Priority */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 0.5,
            textAlign: isRTL ? 'right' : 'left'
          }}>
            <Typography variant="caption" sx={{ 
              fontWeight: 600, 
              color: 'text.secondary',
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {t('documents:documentPriority')}
            </Typography>
            <Chip 
              label={doc.priority} 
              color={getPriorityColor(doc.priority)} 
              size="small" 
              sx={{ 
                alignSelf: isRTL ? 'flex-end' : 'flex-start',
                fontWeight: 600,
                fontSize: '0.75rem'
              }} 
            />
          </Box>
        </Box>

        {/* Document Details */}
        <Stack spacing={2}>
          {/* Reference Number */}
          {doc.reference_number && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              flexDirection: isRTL ? 'row-reverse' : 'row',
              p: 1.5,
              backgroundColor: 'grey.50',
              borderRadius: 2
            }}>
              <Box sx={{ 
                width: 32, 
                height: 32, 
                borderRadius: '50%', 
                backgroundColor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Typography variant="caption" sx={{ color: 'white', fontWeight: 700 }}>
                  #
                </Typography>
              </Box>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="caption" sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {t('documents:referenceNumber')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {doc.reference_number}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Dates Row */}
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2
          }}>
            <Box sx={{ textAlign: isRTL ? 'right' : 'left' }}>
              <Typography variant="caption" sx={{ 
                color: 'text.secondary',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'block',
                mb: 0.5
              }}>
                {t('documents:document_date')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                <LocalizedDate date={doc.document_date} />
              </Typography>
            </Box>

            <Box sx={{ textAlign: isRTL ? 'right' : 'left' }}>
              <Typography variant="caption" sx={{ 
                color: 'text.secondary',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'block',
                mb: 0.5
              }}>
                {t('documents:archive_registration_date')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                <LocalizedDate date={doc.archive_registration_date} />
              </Typography>
            </Box>
          </Box>

          {/* Additional Info Row */}
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 2
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {doc.number_of_pages || '-'}
              </Typography>
              <Typography variant="caption" sx={{ 
                color: 'text.secondary',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {t('documents:number_of_pages')}
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Chip 
                label={t(`documents:source_${doc.source_type.toLowerCase()}`)} 
                size="small" 
                variant="outlined"
                sx={{ 
                  fontWeight: 600,
                  fontSize: '0.7rem'
                }} 
              />
              <Typography variant="caption" sx={{ 
                color: 'text.secondary',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'block',
                mt: 0.5
              }}>
                {t('documents:sourceType')}
              </Typography>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              {taskAssignmentsLoading ? (
                <CircularProgress size={20} />
              ) : (() => {
                const status = getDocumentTaskStatus(doc.id);
                if (!status) return <Typography variant="body2" color="text.secondary">-</Typography>;
                return (
                  <Chip 
                    label={getStatusLabel(status)} 
                    color={getStatusColor(status)} 
                    size="small" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: '0.7rem'
                    }} 
                  />
                );
              })()}
              <Typography variant="caption" sx={{ 
                color: 'text.secondary',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'block',
                mt: 0.5
              }}>
                {t('documents:task_status', 'Task Status')}
              </Typography>
            </Box>
          </Box>

          {/* Archive Number */}
          {doc.archive_number && (
            <Box sx={{ 
              p: 1.5,
              backgroundColor: 'grey.50',
              borderRadius: 2,
              textAlign: isRTL ? 'right' : 'left'
            }}>
              <Typography variant="caption" sx={{ 
                color: 'text.secondary',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {t('documents:archive_number')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {doc.archive_number}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>

      {/* Modern Action Bar */}
      <CardActions sx={{ 
        p: 2, 
        pt: 1,
        backgroundColor: 'grey.50',
        borderTop: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          width: '100%', 
          justifyContent: isRTL ? 'flex-start' : 'flex-end' 
        }}>
          {[
            { 
              icon: <Visibility />, 
              label: t('common:view'), 
              color: 'primary', 
              variant: 'contained',
              to: `/documents/${doc.id}` 
            },
            { 
              icon: <Edit />, 
              label: t('common:edit'), 
              color: 'secondary', 
              variant: 'outlined',
              to: `/documents/${doc.id}/edit` 
            },
            { 
              icon: <Delete />, 
              label: t('common:delete'), 
              color: 'error', 
              variant: 'outlined',
              onClick: () => handleDelete(doc.id) 
            }
          ].map((action, i) => (
            <Tooltip key={i} title={action.label} placement={isRTL ? "right" : "left"}>
              <Button
                variant={action.variant}
                size="small"
                color={action.color}
                component={action.to ? RouterLink : 'button'}
                to={action.to}
                onClick={action.onClick}
                startIcon={action.icon}
                sx={{
                  minWidth: 'auto',
                  px: 2,
                  py: 1,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 2
                  }
                }}
              >
                {action.label}
              </Button>
            </Tooltip>
          ))}
        </Box>
      </CardActions>
    </Card>
  );

  // Helper to get latest status for a document
  const getDocumentTaskStatus = (docId) => {
    if (!taskAssignmentsData || !Array.isArray(taskAssignmentsData)) {
      console.log(`No task assignments data for document ${docId}`);
      return null;
    }
    const assignment = taskAssignmentsData.find(t => t.document_id === docId);
    console.log(`Document ${docId} assignment:`, assignment);
    console.log(`Document ${docId} status:`, assignment?.effective_status || assignment?.status);
    // Use effective_status if available, otherwise fall back to status
    return assignment?.effective_status || assignment?.status || null;
  };

  // Handlers
  const handlePageChange = (_, value) => setPage(value);
  const handleLimitChange = (e) => { setLimit(Number(e.target.value)); setPage(1) };
  const handleSearchChange = (e) => setSearch(e.target.value);
  const handleSearch = (e) => { e.preventDefault(); setPage(1); refetch() };
  const handleFilterChange = () => { setPage(1); refetch() };
  const handleResetFilters = () => {
    setSearch(''); setTypeFilter(''); setTaskStatusFilter(''); setPriorityFilter('');
    setSourceTypeFilter(''); setFromDateFilter(''); setToDateFilter('');
    setIsConfidentialFilter(''); setIsArchivedFilter('');
    setPage(1); refetch();
  };

  // Data processing
  const totalItems = documentsData?.count || documentsData?.total || 0;
  const totalPages = documentsData?.totalPages || Math.max(Math.ceil(totalItems / limit), 1);
  const documents = Array.isArray(documentsData) ? documentsData : 
    documentsData?.data || documentsData?.documents || documentsData?.results || [];

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'} sx={{ p: { xs: 0.5, sm: 3 } }}>
      {/* Professional Header Section */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: isMobile ? 'column' : (isRTL ? 'row-reverse' : 'row'),
        gap: isMobile ? 2 : 0
      }}>
        {/* Title Section */}
        <Typography 
          variant="h4" 
          component="h1"
          sx={{ 
            fontWeight: 600,
            fontSize: { xs: '1.75rem', sm: '2.125rem' },
            color: 'text.primary',
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
            order: isMobile ? 1 : (isRTL ? 2 : 1),
            textAlign: isMobile ? 'center' : (isRTL ? 'right' : 'left')
          }}
        >
          {t('documents:allDocuments')}
        </Typography>

        {/* Action Buttons Section */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1,
          order: isMobile ? 2 : (isRTL ? 1 : 2),
          flexDirection: isMobile ? 'column' : 'row',
          width: isMobile ? '100%' : 'auto',
          alignItems: isMobile ? 'center' : 'flex-end'
        }}>
          <RTLButton 
            variant="contained" 
            startIcon={!isRTL && <Add />} 
            endIcon={isRTL && <Add />}
            component={RouterLink} 
            to="/documents/create" 
            {...getRTLProps('button')} 
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontSize: '0.875rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.2s ease-in-out',
              width: isMobile ? '100%' : 'auto',
              maxWidth: isMobile ? '280px' : 'none'
            }}
          >
            {t('documents:createNew')}
          </RTLButton>
        </Box>
      </Box>

      {/* Professional Search and Filter Section */}
      <Card sx={{ mb: 3, boxShadow: 1 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
          {/* Search Row */}
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 0.5, sm: 2 }, 
            mb: 2,
            flexDirection: isRTL ? 'row-reverse' : 'row',
            alignItems: 'center'
          }}>
            <RTLTextField 
              variant="outlined" 
              size="small" 
              placeholder={t('common:search')} 
              value={search}
              onChange={handleSearchChange} 
              {...getRTLProps('textfield')} 
              sx={{ 
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  }
                }
              }}
              onKeyPress={(e) => { if (e.key === 'Enter') handleSearch(e); }}
              InputProps={{ 
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }} 
            />
            <RTLButton 
              variant="contained" 
              onClick={handleSearch} 
              {...getRTLProps('button')}
              sx={{ 
                minWidth: '120px',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)'
                }
              }}
            >
              {t('common:search')}
            </RTLButton>
          </Box>

          {/* Action Buttons Row */}
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 0.25, sm: 1 }, 
            flexWrap: 'wrap',
            flexDirection: isRTL ? 'row-reverse' : 'row',
            justifyContent: { xs: 'center', sm: 'flex-start' }
          }}>
            <RTLButton 
              variant="outlined" 
              onClick={handleResetFilters} 
              {...getRTLProps('button')}
              sx={{ 
                minWidth: '100px',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                borderColor: 'grey.300',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'primary.50'
                }
              }}
            >
              {t('common:reset')}
            </RTLButton>
            <RTLButton 
              variant="outlined" 
              startIcon={!isRTL && <Refresh />} 
              endIcon={isRTL && <Refresh />}
              onClick={refetch} 
              {...getRTLProps('button')}
              sx={{ 
                minWidth: '100px',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                borderColor: 'grey.300',
                color: 'text.secondary',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'primary.50'
                }
              }}
            >
              {t('common:refresh')}
            </RTLButton>
            <RTLButton 
              variant="text" 
              onClick={() => setShowFilters(!showFilters)} 
              {...getRTLProps('button')}
              startIcon={!isRTL && (showFilters ? <ExpandLess /> : <ExpandMore />)}
              endIcon={isRTL && (showFilters ? <ExpandLess /> : <ExpandMore />)}
              sx={{ 
                minWidth: '100px',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.50'
                }
              }}
            >
              <FilterList fontSize="small" sx={{ mr: 0.5 }} /> {t('common:filter')}
            </RTLButton>
          </Box>
        </CardContent>
      </Card>

      {/* Professional Filter Panel */}
      <Collapse in={showFilters}>
        <Card sx={{ mb: 3, boxShadow: 1 }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
              {t('common:filter_options')}
            </Typography>
            <Grid container spacing={2}>
            {[
              { id: 'type', label: t('documents:type_filter'), value: typeFilter, options: documentTypes, setter: setTypeFilter },
              { id: 'taskStatus', label: t('documents:status_filter'), value: taskStatusFilter, options: taskStatuses, setter: setTaskStatusFilter },
              { id: 'priority', label: t('documents:priority_filter'), value: priorityFilter, options: documentPriorities, setter: setPriorityFilter },
              { id: 'source', label: t('documents:source_type_filter'), value: sourceTypeFilter, options: sourceTypes, setter: setSourceTypeFilter },
              { id: 'confidential', label: t('documents:confidentiality_filter'), value: isConfidentialFilter, 
                options: ['', 'true', 'false'].map(v => ({ value: v, label: v ? t(`documents:${v === 'true' ? 'confidential' : 'public'}`) : t('common:all') })), 
                setter: setIsConfidentialFilter },
              { id: 'archived', label: t('documents:archived_filter'), value: isArchivedFilter, 
                options: ['', 'true', 'false'].map(v => ({ value: v, label: v ? t(`documents:${v === 'true' ? 'archived' : 'active'}`) : t('common:all') })), 
                setter: setIsArchivedFilter }
            ].map((filter, i) => (
              <Grid item xs={12} sm={4} key={filter.id}>
                <RTLFormControl fullWidth size="small" {...getRTLProps('select')}>
                  <InputLabel>{filter.label}</InputLabel>
                  <Select value={filter.value} onChange={(e) => { filter.setter(e.target.value); handleFilterChange() }} label={filter.label}>
                    {filter.options.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                  </Select>
                </RTLFormControl>
              </Grid>
            ))}
            
            <Grid item xs={12} sm={6}>
              <LocalizedDatePicker
                label={t('documents:from_date')}
                value={fromDateFilter || ''}
                onChange={(e) => { 
                  const newValue = e.target.value || '';
                  setFromDateFilter(newValue); 
                  // Only trigger filter change if a date is actually selected
                  if (newValue && newValue.trim() !== '') {
                    console.log('From date filter changed to:', newValue);
                    handleFilterChange(); 
                  } else {
                    console.log('From date filter cleared');
                    handleFilterChange();
                  }
                }}
                name="from_date"
                isRtl={isRTL}
                inputProps={{
                  size: 'small',
                  sx: {
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'divider'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <LocalizedDatePicker
                label={t('documents:to_date')}
                value={toDateFilter || ''}
                onChange={(e) => { 
                  const newValue = e.target.value || '';
                  setToDateFilter(newValue); 
                  // Only trigger filter change if a date is actually selected
                  if (newValue && newValue.trim() !== '') {
                    console.log('To date filter changed to:', newValue);
                    handleFilterChange(); 
                  } else {
                    console.log('To date filter cleared');
                    handleFilterChange();
                  }
                }}
                name="to_date"
                isRtl={isRTL}
                inputProps={{
                  size: 'small',
                  sx: {
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'divider'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {/* Error handling */}
      {isError && <Alert severity="error" sx={{ mb: 3, textAlign: isRTL ? 'right' : 'left' }}>{error?.message || t('error_occurred')}</Alert>}

      {/* Document Display - Mobile Cards / Desktop Table */}
      {isMobile ? (
        // Mobile Card View
        <Box sx={{ width: '100%', mb: 3 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : documents.length === 0 ? (
            <Card sx={{ p: 4, textAlign: 'center', borderRadius: 2, boxShadow: 1 }}>
              <Typography variant="h6" color="text.secondary">
                {t('common:no_records')}
              </Typography>
            </Card>
          ) : (
            <Box>
              {documents.map((doc, i) => (
                <DocumentCard key={doc.id} doc={doc} index={i} />
              ))}
            </Box>
          )}

          {/* Mobile Pagination */}
          <Card sx={{ p: { xs: 1, sm: 2 }, borderRadius: 2, mt: 2, boxShadow: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
              {/* Rows per page selector */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {t('common:rows_per_page')}:
                </Typography>
                <Select value={limit} onChange={handleLimitChange} size="small" sx={{ minWidth: 70 }}>
                  {[5, 10, 25, 50].map(num => <MenuItem key={num} value={num}>{num}</MenuItem>)}
                </Select>
              </Box>
              
              {/* Pagination info and controls */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  {t('common:showing')} {(page - 1) * limit + 1}-{Math.min(page * limit, totalItems)} {t('common:of')} {totalItems}
                </Typography>
                <Pagination 
                  count={Math.max(totalPages, 1)} 
                  page={page} 
                  onChange={handlePageChange} 
                  color="primary" 
                  dir={isRTL ? 'rtl' : 'ltr'} 
                  shape="rounded" 
                  size="small"
                  showFirstButton 
                  showLastButton 
                />
              </Box>
            </Box>
          </Card>
        </Box>
      ) : (
        // Desktop Table View
        <Card sx={{ width: '100%', mb: 3, boxShadow: 1 }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead sx={{ bgcolor: theme.palette.grey[100] }}>
                <TableRow>
                  {[
                    { id: 'id', label: t('common:id'), width: '50px' },
                    { id: 'title', label: t('documents:title'), width: '18%' },
                    { id: 'info', label: t('documents:document_info'), width: '18%' },
                    { id: 'archive', label: t('documents:archive_info'), width: '18%' },
                    { id: 'pages', label: t('documents:number_of_pages'), width: '6%' },
                    { id: 'type', label: t('documents:documentType'), width: '8%' },
                    { id: 'priority', label: t('documents:documentPriority'), width: '8%' },
                    { id: 'source', label: t('documents:sourceType'), width: '8%' },
                    { id: 'task_status', label: t('documents:task_status', 'Task Status'), width: '8%', align: 'center' },
                    { id: 'actions', label: t('common:actions'), width: '100px', align: 'right' }
                  ].map(col => (
                    <TableCell key={col.id} align={col.align} sx={{ width: col.width, textAlign: isRTL ? 'right' : 'left', fontWeight: 'bold' }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3 }}><CircularProgress size={30} /></TableCell></TableRow>
                ) : documents.length === 0 ? (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3 }}>{t('common:no_records')}</TableCell></TableRow>
                ) : (
                  documents.map((doc, i) => (
                    <TableRow key={doc.id} hover sx={{ '& td': { py: 1.5 } }}>
                      <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}><Typography variant="body2" sx={{ fontWeight: 500 }}>{(page - 1) * limit + i + 1}</Typography></TableCell>
                      <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{doc.title}</Typography>
                          {doc.is_confidential && <Chip label={t('documents:confidential')} color="error" size="small" sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {t('documents:document_date')}: <LocalizedDate date={doc.document_date} />
                          </Typography>
                          <Typography variant="body2">
                            {t('documents:referenceNumber')}: <Typography component="span" color="text.secondary" sx={{ ml: 0.5 }}>{doc.reference_number || '-'}</Typography>
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {t('documents:archive_registration_date')}: <LocalizedDate date={doc.archive_registration_date} />
                          </Typography>
                          <Typography variant="body2">
                            {t('documents:archive_number')}: <Typography component="span" color="text.secondary" sx={{ ml: 0.5 }}>{doc.archive_number || '-'}</Typography>
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ textAlign: 'center' }}><Typography variant="body2" sx={{ fontWeight: 500 }}>{doc.number_of_pages || '-'}</Typography></TableCell>
                      <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}><Typography variant="body2">{doc.type}</Typography></TableCell>
                      <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}>
                        <Chip label={doc.priority} color={getPriorityColor(doc.priority)} size="small" />
                      </TableCell>
                      <TableCell sx={{ textAlign: isRTL ? 'right' : 'left' }}><Chip label={t(`documents:source_${doc.source_type.toLowerCase()}`)} size="small" variant="outlined" /></TableCell>
                      <TableCell align="center" sx={{ textAlign: 'center' }}>
                        {taskAssignmentsLoading ? <CircularProgress size={18} /> :
                          (() => {
                            const status = getDocumentTaskStatus(doc.id);
                            if (!status) return '-';
                            return <Chip label={getStatusLabel(status)} color={getStatusColor(status)} size="small" />;
                          })()
                        }
                      </TableCell>
                      <TableCell align="right" sx={{ textAlign: isRTL ? 'left' : 'right' }}>
                        <Box sx={{ display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end', gap: 1 }}>
                          {[
                            { icon: <Visibility />, label: t('common:view'), color: 'info', to: `/documents/${doc.id}` },
                            { icon: <Edit />, label: t('common:edit'), color: 'primary', to: `/documents/${doc.id}/edit` },
                            { icon: <Delete />, label: t('common:delete'), color: 'error', onClick: () => handleDelete(doc.id) }
                          ].map((action, i) => (
                            <Tooltip key={i} title={action.label} placement={isRTL ? "right" : "left"}>
                              <IconButton size="small" color={action.color} component={action.to ? RouterLink : undefined} 
                                to={action.to} onClick={action.onClick}>
                                {action.icon}
                              </IconButton>
                            </Tooltip>
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Desktop Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, 
            flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {t('common:rows_per_page')}:
              </Typography>
              <Select value={limit} onChange={handleLimitChange} size="small" sx={{ minWidth: 70 }}>
                {[5, 10, 25, 50].map(num => <MenuItem key={num} value={num}>{num}</MenuItem>)}
              </Select>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {t('common:showing')} {(page - 1) * limit + 1}-{Math.min(page * limit, totalItems)} {t('common:of')} {totalItems}
              </Typography>
              <Pagination count={Math.max(totalPages, 1)} page={page} onChange={handlePageChange} 
                color="primary" dir={isRTL ? 'rtl' : 'ltr'} shape="rounded" size="small" 
                showFirstButton showLastButton />
            </Box>
          </Box>
        </Card>
      )}

      <ConfirmDialog message={t('documents:delete_confirm')} />
    </Box>
  );
};

export default DocumentList;